import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Minus, X, Zap, AlertTriangle, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import { useToast } from '../components/Toast';

// ─── API endpoints ─────────────────────────────────────────────────────────────
const DYDX_API    = 'https://indexer.dydx.trade/v4';
const ORDERLY_API = 'https://api.orderly.org/v1/public';
// Public WS — real account_id required (zero address no longer accepted by Orderly)
const ORD_WS_URL  = 'wss://ws-evm.orderly.org/ws/stream/0x55e49c928fb1e75bf500edeefeb8c2b94615f62bc45fda71d5671d40c13b623d';

// ─── Fees ──────────────────────────────────────────────────────────────────────
const DYDX_MAKER    = 0.0002;
const DYDX_TAKER    = 0.0005;
const ORDERLY_MAKER = 0.0000;   // FREE
const ORDERLY_TAKER = 0.00045;

// ─── Asset map ─────────────────────────────────────────────────────────────────
interface Asset { symbol: string; dydxTicker: string; orderlySymbol: string; multiplier: number; }

const ASSETS: Asset[] = [
  // ── Major ──────────────────────────────────────────────────────────────────
  { symbol: 'BTC',     dydxTicker: 'BTC-USD',     orderlySymbol: 'PERP_BTC_USDC',        multiplier: 1    },
  { symbol: 'ETH',     dydxTicker: 'ETH-USD',     orderlySymbol: 'PERP_ETH_USDC',        multiplier: 1    },
  { symbol: 'SOL',     dydxTicker: 'SOL-USD',     orderlySymbol: 'PERP_SOL_USDC',        multiplier: 1    },
  { symbol: 'BNB',     dydxTicker: 'BNB-USD',     orderlySymbol: 'PERP_BNB_USDC',        multiplier: 1    },
  { symbol: 'XRP',     dydxTicker: 'XRP-USD',     orderlySymbol: 'PERP_XRP_USDC',        multiplier: 1    },
  { symbol: 'DOGE',    dydxTicker: 'DOGE-USD',    orderlySymbol: 'PERP_DOGE_USDC',       multiplier: 1    },
  { symbol: 'ADA',     dydxTicker: 'ADA-USD',     orderlySymbol: 'PERP_ADA_USDC',        multiplier: 1    },
  { symbol: 'AVAX',    dydxTicker: 'AVAX-USD',    orderlySymbol: 'PERP_AVAX_USDC',       multiplier: 1    },
  { symbol: 'LTC',     dydxTicker: 'LTC-USD',     orderlySymbol: 'PERP_LTC_USDC',        multiplier: 1    },
  { symbol: 'LINK',    dydxTicker: 'LINK-USD',    orderlySymbol: 'PERP_LINK_USDC',       multiplier: 1    },
  { symbol: 'DOT',     dydxTicker: 'DOT-USD',     orderlySymbol: 'PERP_DOT_USDC',        multiplier: 1    },
  { symbol: 'BCH',     dydxTicker: 'BCH-USD',     orderlySymbol: 'PERP_BCH_USDC',        multiplier: 1    },
  { symbol: 'NEAR',    dydxTicker: 'NEAR-USD',    orderlySymbol: 'PERP_NEAR_USDC',       multiplier: 1    },
  { symbol: 'APT',     dydxTicker: 'APT-USD',     orderlySymbol: 'PERP_APT_USDC',        multiplier: 1    },
  { symbol: 'SUI',     dydxTicker: 'SUI-USD',     orderlySymbol: 'PERP_SUI_USDC',        multiplier: 1    },
  { symbol: 'TRX',     dydxTicker: 'TRX-USD',     orderlySymbol: 'PERP_TRX_USDC',        multiplier: 1    },
  { symbol: 'TON',     dydxTicker: 'TON-USD',     orderlySymbol: 'PERP_TON_USDC',        multiplier: 1    },
  { symbol: 'TAO',     dydxTicker: 'TAO-USD',     orderlySymbol: 'PERP_TAO_USDC',        multiplier: 1    },
  // ── Meme / High-vol ────────────────────────────────────────────────────────
  { symbol: 'BONK',    dydxTicker: 'BONK-USD',    orderlySymbol: 'PERP_1000BONK_USDC',  multiplier: 1000 },
  { symbol: 'PEPE',    dydxTicker: 'PEPE-USD',    orderlySymbol: 'PERP_1000PEPE_USDC',  multiplier: 1000 },
  { symbol: 'SHIB',    dydxTicker: 'SHIB-USD',    orderlySymbol: 'PERP_1000SHIB_USDC',  multiplier: 1000 },
  { symbol: 'WIF',     dydxTicker: 'WIF-USD',     orderlySymbol: 'PERP_WIF_USDC',        multiplier: 1    },
  { symbol: 'TRUMP',   dydxTicker: 'TRUMP-USD',   orderlySymbol: 'PERP_TRUMP_USDC',      multiplier: 1    },
  { symbol: 'PENGU',   dydxTicker: 'PENGU-USD',   orderlySymbol: 'PERP_PENGU_USDC',      multiplier: 1    },
  // ── DeFi ───────────────────────────────────────────────────────────────────
  { symbol: 'AAVE',    dydxTicker: 'AAVE-USD',    orderlySymbol: 'PERP_AAVE_USDC',       multiplier: 1    },
  { symbol: 'CRV',     dydxTicker: 'CRV-USD',     orderlySymbol: 'PERP_CRV_USDC',        multiplier: 1    },
  { symbol: 'SNX',     dydxTicker: 'SNX-USD',     orderlySymbol: 'PERP_SNX_USDC',        multiplier: 1    },
  { symbol: 'LDO',     dydxTicker: 'LDO-USD',     orderlySymbol: 'PERP_LDO_USDC',        multiplier: 1    },
  { symbol: 'UNI',     dydxTicker: 'UNI-USD',     orderlySymbol: 'PERP_UNI_USDC',        multiplier: 1    },
  { symbol: 'ENA',     dydxTicker: 'ENA-USD',     orderlySymbol: 'PERP_ENA_USDC',        multiplier: 1    },
  { symbol: 'ETHFI',   dydxTicker: 'ETHFI-USD',   orderlySymbol: 'PERP_ETHFI_USDC',      multiplier: 1    },
  { symbol: 'MORPHO',  dydxTicker: 'MORPHO-USD',  orderlySymbol: 'PERP_MORPHO_USDC',     multiplier: 1    },
  { symbol: 'PENDLE',  dydxTicker: 'PENDLE-USD',  orderlySymbol: 'PERP_PENDLE_USDC',     multiplier: 1    },
  { symbol: 'WOO',     dydxTicker: 'WOO-USD',     orderlySymbol: 'PERP_WOO_USDC',        multiplier: 1    },
  // ── L1/L2 ──────────────────────────────────────────────────────────────────
  { symbol: 'ARB',     dydxTicker: 'ARB-USD',     orderlySymbol: 'PERP_ARB_USDC',        multiplier: 1    },
  { symbol: 'OP',      dydxTicker: 'OP-USD',      orderlySymbol: 'PERP_OP_USDC',         multiplier: 1    },
  { symbol: 'POL',     dydxTicker: 'POL-USD',     orderlySymbol: 'PERP_POL_USDC',        multiplier: 1    },
  { symbol: 'MNT',     dydxTicker: 'MNT-USD',     orderlySymbol: 'PERP_MNT_USDC',        multiplier: 1    },
  { symbol: 'SEI',     dydxTicker: 'SEI-USD',     orderlySymbol: 'PERP_SEI_USDC',        multiplier: 1    },
  { symbol: 'TIA',     dydxTicker: 'TIA-USD',     orderlySymbol: 'PERP_TIA_USDC',        multiplier: 1    },
  { symbol: 'INJ',     dydxTicker: 'INJ-USD',     orderlySymbol: 'PERP_INJ_USDC',        multiplier: 1    },
  { symbol: 'AXS',     dydxTicker: 'AXS-USD',     orderlySymbol: 'PERP_AXS_USDC',        multiplier: 1    },
  // ── Infra / Other ──────────────────────────────────────────────────────────
  { symbol: 'JUP',     dydxTicker: 'JUP-USD',     orderlySymbol: 'PERP_JUP_USDC',        multiplier: 1    },
  { symbol: 'FIL',     dydxTicker: 'FIL-USD',     orderlySymbol: 'PERP_FIL_USDC',        multiplier: 1    },
  { symbol: 'ICP',     dydxTicker: 'ICP-USD',     orderlySymbol: 'PERP_ICP_USDC',        multiplier: 1    },
  { symbol: 'HBAR',    dydxTicker: 'HBAR-USD',    orderlySymbol: 'PERP_HBAR_USDC',       multiplier: 1    },
  { symbol: 'ONDO',    dydxTicker: 'ONDO-USD',    orderlySymbol: 'PERP_ONDO_USDC',       multiplier: 1    },
  { symbol: 'CAKE',    dydxTicker: 'CAKE-USD',    orderlySymbol: 'PERP_CAKE_USDC',       multiplier: 1    },
  { symbol: 'WLD',     dydxTicker: 'WLD-USD',     orderlySymbol: 'PERP_WLD_USDC',        multiplier: 1    },
  { symbol: 'ZRO',     dydxTicker: 'ZRO-USD',     orderlySymbol: 'PERP_ZRO_USDC',        multiplier: 1    },
  { symbol: 'ZEC',     dydxTicker: 'ZEC-USD',     orderlySymbol: 'PERP_ZEC_USDC',        multiplier: 1    },
  { symbol: 'ZEN',     dydxTicker: 'ZEN-USD',     orderlySymbol: 'PERP_ZEN_USDC',        multiplier: 1    },
  { symbol: 'XMR',     dydxTicker: 'XMR-USD',     orderlySymbol: 'PERP_XMR_USDC',        multiplier: 1    },
  // ── Commodities / Alt ──────────────────────────────────────────────────────
  { symbol: 'PAXG',    dydxTicker: 'PAXG-USD',    orderlySymbol: 'PERP_PAXG_USDC',       multiplier: 1    },
];

// ─── Types ─────────────────────────────────────────────────────────────────────
interface DydxMarket { ticker: string; nextFundingRate: string; oraclePrice: string; initialMarginFraction: string; }
interface DydxOrderbook { bids: { price: string; size: string }[]; asks: { price: string; size: string }[]; }
interface OrderlyFuture {
  symbol: string; mark_price: number; index_price: number;
  est_funding_rate: number; last_funding_rate: number; next_funding_time: number;
}
interface OrdBbo { bid: number; bidSize: number; ask: number; askSize: number; }
type BboMap = Record<string, OrdBbo>;  // keyed by orderlySymbol
type BestDir = 'S_DYDX_L_ORD' | 'L_DYDX_S_ORD' | 'NONE';
type SizeUnit = 'USDC' | 'TOKEN';

interface Row {
  symbol: string; dydxTicker: string; orderlySymbol: string; multiplier: number;
  dydxBid: number; dydxAsk: number; dydxFunding: number; dydxSpreadPct: number;
  orderlyMid: number; orderlyFunding: number; orderlyFundingRaw: number;
  orderlyNextFundingTime: number;
  netShortDydxLongOrd: number; netLongDydxShortOrd: number;
  bestRate: number; bestDir: BestDir;
  dydxMaxLev: number; ordMaxLev: number;
}

interface ModalState {
  dydxDir: 'LONG' | 'SHORT'; ordDir: 'LONG' | 'SHORT';
  dydxPrice: string; ordPrice: string;
  sizeRaw: string; sizeUnit: SizeUnit;
  dydxLev: number; ordLev: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const pct = (n: number, d = 4) => `${(n * 100).toFixed(d)}%`;
const fmt = (n: number) =>
  n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  : n >= 1  ? n.toFixed(4)
  : n.toFixed(6);

// ─── Sub-components ────────────────────────────────────────────────────────────
function FundingBadge({ rate }: { rate: number }) {
  const color = rate > 0.00001 ? 'text-emerald-400' : rate < -0.00001 ? 'text-rose-400' : 'text-blue-400';
  const icon  = rate > 0.00001 ? <TrendingUp size={10} /> : rate < -0.00001 ? <TrendingDown size={10} /> : <Minus size={10} />;
  return <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${color}`}>{icon}{pct(rate, 5)}</span>;
}

const LEVERAGES = [1, 2, 3, 5, 10, 20];

function DirToggle({ value, onChange, exchange }: {
  value: 'LONG' | 'SHORT'; onChange: (d: 'LONG' | 'SHORT') => void; exchange: 'dydx' | 'ord';
}) {
  const accent = exchange === 'dydx' ? 'border-blue-500/60' : 'border-emerald-500/60';
  return (
    <div className={`flex rounded-lg border overflow-hidden text-xs font-black ${accent}`}>
      <button onClick={() => onChange('LONG')}
        className={`flex-1 py-1.5 transition-colors ${value === 'LONG' ? 'bg-emerald-500/30 text-emerald-300' : 'bg-[#061530] text-blue-600 hover:text-emerald-500'}`}
      >LONG</button>
      <button onClick={() => onChange('SHORT')}
        className={`flex-1 py-1.5 border-l border-[#0a2560] transition-colors ${value === 'SHORT' ? 'bg-rose-500/30 text-rose-300' : 'bg-[#061530] text-blue-600 hover:text-rose-500'}`}
      >SHORT</button>
    </div>
  );
}

function LevPicker({ value, onChange }: { value: number; onChange: (l: number) => void }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {LEVERAGES.map(lev => (
        <button key={lev} onClick={() => onChange(lev)}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
            value === lev ? 'bg-[#4A9FE8]/20 border-[#4A9FE8] text-[#4A9FE8]'
              : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-blue-500 hover:text-blue-300'
          }`}
        >{lev}×</button>
      ))}
    </div>
  );
}

// ─── Order Modal ───────────────────────────────────────────────────────────────
function OrderModal({ row, bbo, dark, onClose, sendWs }: {
  row: Row; bbo: OrdBbo | undefined; dark: boolean; onClose: () => void;
  sendWs: (msg: object) => void;
}) {
  const t = getTheme(dark);
  const { showToast } = useToast();
  const [sending,  setSending]  = useState(false);
  const [postOnly, setPostOnly] = useState(true);
  const defDydxDir: 'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_ORD' ? 'SHORT' : 'LONG';
  const defOrdDir:  'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_ORD' ? 'LONG'  : 'SHORT';
  const priceStr = (p: number) => p.toFixed(p >= 100 ? 2 : 4);

  // Orderly bid/ask — real from WS, fallback to mark ± 0.01%
  const ordBid = bbo ? bbo.bid / row.multiplier : row.orderlyMid * 0.9999;
  const ordAsk = bbo ? bbo.ask / row.multiplier : row.orderlyMid * 1.0001;

  const [modal, setModal] = useState<ModalState>({
    dydxDir:   defDydxDir,
    ordDir:    defOrdDir,
    dydxPrice: priceStr(defDydxDir === 'LONG' ? row.dydxBid : row.dydxAsk),
    ordPrice:  priceStr(defOrdDir  === 'LONG' ? ordBid      : ordAsk),
    sizeRaw:   '1000',
    sizeUnit:  'USDC',
    dydxLev:   3,
    ordLev:    3,
  });

  const setDydxDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, dydxDir: d, dydxPrice: priceStr(d === 'LONG' ? row.dydxBid : row.dydxAsk),
  }));
  const setOrdDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, ordDir: d, ordPrice: priceStr(d === 'LONG' ? ordBid : ordAsk),
  }));

  const dirColor = (d: string) => d === 'LONG' ? 'text-emerald-400' : 'text-rose-400';
  const dirBg    = (d: string) => d === 'LONG' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30';

  const midPrice = (row.dydxBid + row.dydxAsk) / 2 || 1;
  const rawVal   = parseFloat(modal.sizeRaw) || 0;
  const sizeUsd  = modal.sizeUnit === 'USDC' ? rawVal : rawVal * midPrice;
  const tokenAmt = modal.sizeUnit === 'TOKEN' ? rawVal : rawVal / midPrice;
  const tokenDec = midPrice > 1000 ? 6 : midPrice > 1 ? 4 : 2;

  const toggleUnit = () => setModal(m => {
    const cur = parseFloat(m.sizeRaw) || 0;
    return m.sizeUnit === 'USDC'
      ? { ...m, sizeUnit: 'TOKEN', sizeRaw: (midPrice > 0 ? cur / midPrice : 0).toFixed(tokenDec) }
      : { ...m, sizeUnit: 'USDC',  sizeRaw: (cur * midPrice).toFixed(2) };
  });

  const entryFeePct  = DYDX_MAKER + ORDERLY_MAKER;
  const exitFeePct   = DYDX_MAKER + ORDERLY_MAKER;
  const roundTrip    = entryFeePct + exitFeePct;
  const earnPerHour  = row.bestRate * sizeUsd;
  const breakEvenH   = row.bestRate > 0 ? roundTrip / row.bestRate : Infinity;
  const breakEvenMin = isFinite(breakEvenH) ? breakEvenH * 60 : null;
  const nextFundingIn = row.orderlyNextFundingTime
    ? Math.max(0, Math.round((row.orderlyNextFundingTime - Date.now()) / 60000))
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className={`w-full max-w-2xl rounded-2xl border ${t.cardBorder} bg-[#030d1f] shadow-2xl`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0a2560]">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-[#4A9FE8]">{row.symbol}</span>
            <span className="text-[11px] text-blue-500">funding arb</span>
            {row.bestRate > 0 && (
              <span className="text-[11px] font-mono text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded-full border border-emerald-700/40">
                +{pct(row.bestRate, 4)}/h
              </span>
            )}
            <span className="text-[11px] text-blue-600 ml-1">{modal.dydxDir} dYdX · {modal.ordDir} Orderly</span>
            {bbo && <span className="text-[9px] text-emerald-600 ml-1">● live BBO</span>}
          </div>
          <button onClick={onClose} className="text-blue-500 hover:text-white transition-colors p-1"><X size={16} /></button>
        </div>

        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">

            {/* dYdX */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.dydxDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">dYdX</span>
                <span className="text-[10px] text-blue-600">Post-Only · Maker {pct(DYDX_MAKER, 3)}</span>
              </div>
              <DirToggle value={modal.dydxDir} onChange={setDydxDir} exchange="dydx" />
              <div>
                <input
                  type="number" value={modal.dydxPrice}
                  onChange={e => setModal(m => ({ ...m, dydxPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.dydxPrice === priceStr(row.dydxBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-emerald-600 hover:text-emerald-400'}`}
                  >Bid {fmt(row.dydxBid)}</button>
                  <button onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.dydxPrice === priceStr(row.dydxAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-rose-600 hover:text-rose-400'}`}
                  >Ask {fmt(row.dydxAsk)}</button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.dydxMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.dydxMaxLev}×</span>}
                </div>
                <LevPicker value={modal.dydxLev} onChange={lev => setModal(m => ({ ...m, dydxLev: lev }))} />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h</span>
                <FundingBadge rate={row.dydxFunding} />
              </div>
            </div>

            {/* Orderly */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.ordDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-wider">Orderly (Ray)</span>
                <span className="text-[10px] text-blue-600">
                  {bbo ? '● Live BBO' : '○ Mark bazlı'} · Maker FREE
                </span>
              </div>
              <DirToggle value={modal.ordDir} onChange={setOrdDir} exchange="ord" />
              <div>
                <input
                  type="number" value={modal.ordPrice}
                  onChange={e => setModal(m => ({ ...m, ordPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setModal(m => ({ ...m, ordPrice: priceStr(ordBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.ordPrice === priceStr(ordBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-emerald-700 hover:border-emerald-600 hover:text-emerald-400'}`}
                  >{bbo ? '' : '~'}Bid {fmt(ordBid)}</button>
                  <button onClick={() => setModal(m => ({ ...m, ordPrice: priceStr(ordAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.ordPrice === priceStr(ordAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-emerald-700 hover:border-rose-600 hover:text-rose-400'}`}
                  >{bbo ? '' : '~'}Ask {fmt(ordAsk)}</button>
                </div>
                {bbo && (
                  <div className="flex justify-between mt-0.5 text-[9px] text-blue-600">
                    <span>Size: {bbo.bidSize.toFixed(2)}</span>
                    <span>Size: {bbo.askSize.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.ordMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.ordMaxLev}×</span>}
                </div>
                <LevPicker value={modal.ordLev} onChange={lev => setModal(m => ({ ...m, ordLev: lev }))} />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h (÷8)</span>
                <FundingBadge rate={row.orderlyFunding} />
              </div>
              {nextFundingIn !== null && (
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-blue-500">Sonraki funding</span>
                  <span className="font-mono text-blue-400">~{nextFundingIn} dk</span>
                </div>
              )}
            </div>
          </div>

          {/* Size */}
          <div>
            <div className="flex items-center gap-2">
              <input
                type="number" value={modal.sizeRaw}
                onChange={e => setModal(m => ({ ...m, sizeRaw: e.target.value }))}
                className="flex-1 bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                placeholder={modal.sizeUnit === 'USDC' ? '1000' : '0.01'}
              />
              <button onClick={toggleUnit} className="flex-shrink-0 flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
                <span className={`px-2.5 py-1.5 transition-colors ${modal.sizeUnit === 'USDC' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>USDC</span>
                <span className={`px-2.5 py-1.5 border-l border-[#0a2560] transition-colors ${modal.sizeUnit === 'TOKEN' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>{row.symbol}</span>
              </button>
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-blue-600">
              {modal.sizeUnit === 'USDC'
                ? <span>≈ <span className="font-mono text-blue-400">{tokenAmt.toFixed(tokenDec)} {row.symbol}</span></span>
                : <span>≈ <span className="font-mono text-blue-400">${sizeUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })} USDC</span></span>
              }
              <span>Toplam: <span className="font-mono text-blue-400">${(sizeUsd * 2).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span></span>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-[#061530]/80 rounded-xl border border-[#0a2560] px-3 py-2">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[11px]">
              <div className="flex justify-between">
                <span className="text-blue-500">Round-trip fee</span>
                <span className="font-mono text-rose-400">−{pct(roundTrip, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Net / saat</span>
                <span className="font-mono text-emerald-400">+{pct(row.bestRate, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Break-even</span>
                <span className="font-mono text-[#4A9FE8]">{breakEvenMin !== null ? `~${breakEvenMin.toFixed(0)} dk` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Saatlik gelir</span>
                <span className="font-mono text-emerald-400">{sizeUsd > 0 ? `$${earnPerHour.toFixed(3)}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">dYdX notional ×{modal.dydxLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.dydxLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Orderly notional ×{modal.ordLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.ordLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
            </div>
          </div>

          {/* Maker / Taker toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-500">Emir tipi</span>
            <div className="flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
              <button
                onClick={() => setPostOnly(true)}
                className={`px-3 py-1.5 transition-colors ${postOnly ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Maker (Post-Only)</button>
              <button
                onClick={() => setPostOnly(false)}
                className={`px-3 py-1.5 border-l border-[#0a2560] transition-colors ${!postOnly ? 'bg-rose-500/20 text-rose-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Taker (Limit)</button>
            </div>
          </div>

          {/* Warning */}
          <div className="flex items-center gap-1.5 text-[10px] text-yellow-600">
            <AlertTriangle size={11} className="flex-shrink-0" />
            <span>{postOnly ? 'Post-only reddedilebilir, Orderly maker için BBO içine girilmeli.' : 'Taker emir piyasada dolar, yüksek fee uygulanır.'} Fonlama periyotta değişebilir. Kısmi dolum riski.</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              disabled={sending}
              className="flex-1 py-2 rounded-xl border border-[#0a2560] text-blue-400 text-xs font-semibold hover:bg-[#0a2560]/40 transition-colors disabled:opacity-40"
            >İptal</button>
            <button
              disabled={sending || !tokenAmt || tokenAmt <= 0}
              onClick={async () => {
                if (sending) return;
                setSending(true);
                try {
                  sendWs({
                    type: 'manual_arb_order_ray',
                    payload: {
                      post_only: postOnly,
                      dydx: {
                        symbol:   row.dydxTicker,
                        side:     modal.dydxDir.toLowerCase(),
                        price:    parseFloat(modal.dydxPrice),
                        quantity: parseFloat(tokenAmt.toFixed(8)),
                        leverage: modal.dydxLev,
                      },
                      ord: {
                        symbol:   row.orderlySymbol,
                        side:     modal.ordDir.toLowerCase(),
                        // BONK gibi pair'lerde Orderly miktar = tokenAmt × multiplier
                        quantity: parseFloat((tokenAmt * row.multiplier).toFixed(8)),
                        price:    parseFloat(modal.ordPrice),
                        leverage: modal.ordLev,
                      },
                    },
                  });
                  await new Promise<void>((resolve) => {
                    const handler = (e: MessageEvent) => {
                      try {
                        const d = JSON.parse(e.data);
                        if (d.type === 'arb_order_result_ray') {
                          window.removeEventListener('ws_message', handler as any);
                          if (d.payload?.success) {
                            showToast('⚡ Arb Emirleri Gönderildi', `dYdX ${modal.dydxDir} + Orderly ${modal.ordDir} — ${row.symbol}`, 'success');
                            onClose();
                          } else {
                            const dErr = d.payload?.dydx?.error;
                            const oErr = d.payload?.ord?.error;
                            showToast('Emir Hatası', [dErr, oErr].filter(Boolean).join(' | '), 'error');
                          }
                          resolve();
                        }
                      } catch {}
                    };
                    window.addEventListener('ws_message', handler as any);
                    setTimeout(() => { window.removeEventListener('ws_message', handler as any); resolve(); }, 20000);
                  });
                } finally {
                  setSending(false);
                }
              }}
              className="flex-1 py-2 rounded-xl bg-[#4A9FE8]/10 border border-[#4A9FE8]/30 text-[#4A9FE8] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#4A9FE8]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> Gönderiliyor...</>
                : <><Zap size={13} /> Her İki Emri Gönder</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Arb Button ────────────────────────────────────────────────────────────────
function ArbButton({ row, onOpen }: { row: Row; onOpen: (r: Row) => void }) {
  if (row.bestDir === 'NONE') return <span className="text-blue-600 text-xs">—</span>;
  const hot   = row.bestRate > 0.0001;
  const label = row.bestDir === 'S_DYDX_L_ORD' ? 'S dYdX / L Ord' : 'L dYdX / S Ord';
  return (
    <button onClick={() => onOpen(row)} className={`flex flex-col items-end gap-0.5 group transition-all ${hot ? 'opacity-100' : 'opacity-75 hover:opacity-100'}`}>
      <span className={`text-[11px] font-mono font-bold px-2 py-1 rounded-lg border transition-all ${
        hot ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 group-hover:bg-emerald-500/35 group-hover:border-emerald-400'
            : 'bg-[#0a2560]/60 text-blue-400 border-[#0a2560] group-hover:border-blue-500 group-hover:text-blue-300'
      }`}>{pct(row.bestRate, 4)}/h ↗</span>
      <span className="text-[9px] text-blue-600 group-hover:text-blue-400 transition-colors">{label}</span>
    </button>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
type Tab = 'dashboard' | 'performans';

export default function DydxRay({ dark }: { dark: boolean }) {
  const { sendWs } = useWS() as any;
  const t = getTheme(dark);
  const [tab,        setTab]        = useState<Tab>('dashboard');
  const [rows,       setRows]       = useState<Row[]>([]);
  const [bboMap,     setBboMap]     = useState<BboMap>({});
  const [wsStatus,   setWsStatus]   = useState<'connecting' | 'live' | 'offline'>('connecting');
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [modalRow,   setModalRow]   = useState<Row | null>(null);
  const wsRef   = useRef<WebSocket | null>(null);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Orderly BBO WebSocket ──────────────────────────────────────────────────
  const connectBboWs = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    setWsStatus('connecting');
    const ws = new WebSocket(ORD_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus('live');
      // Subscribe to all-symbols BBO (1s updates, no auth needed)
      ws.send(JSON.stringify({ id: 'bbos-sub', topic: 'bbos', event: 'subscribe' }));
      // Heartbeat
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ event: 'ping' }));
      }, 10_000);
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.topic === 'bbos' && Array.isArray(msg.data)) {
          setBboMap(prev => {
            const next = { ...prev };
            msg.data.forEach((item: any) => {
              if (item.bid && item.ask) {
                next[item.symbol] = { bid: item.bid, bidSize: item.bidSize ?? 0, ask: item.ask, askSize: item.askSize ?? 0 };
              }
            });
            return next;
          });
        }
      } catch { /* ignore parse errors */ }
    };

    ws.onerror = () => setWsStatus('offline');
    ws.onclose = () => {
      setWsStatus('offline');
      if (pingRef.current) clearInterval(pingRef.current);
      // Reconnect after 5s
      setTimeout(connectBboWs, 5_000);
    };
  }, []);

  useEffect(() => {
    connectBboWs();
    return () => {
      wsRef.current?.close();
      if (pingRef.current) clearInterval(pingRef.current);
    };
  }, [connectBboWs]);

  // ── REST data fetch ────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [dydxRes, ordRes] = await Promise.all([
        fetch(`${DYDX_API}/perpetualMarkets`),
        fetch(`${ORDERLY_API}/futures`),
      ]);
      const dydxData = await dydxRes.json();
      const ordData  = await ordRes.json();
      const dydxMap: Record<string, DydxMarket>    = dydxData.markets;
      const ordMap:  Record<string, OrderlyFuture> = {};
      (ordData.data?.rows ?? []).forEach((f: OrderlyFuture) => { ordMap[f.symbol] = f; });

      const validAssets = ASSETS.filter(a => dydxMap[a.dydxTicker] && ordMap[a.orderlySymbol]);

      // Fetch Orderly base_imr (max leverage) for each valid pair
      const ordInfoResults = await Promise.allSettled(
        validAssets.map(a => fetch(`${ORDERLY_API}/info/${a.orderlySymbol}`).then(r => r.json()))
      );
      const ordMaxLevMap: Record<string, number> = {};
      ordInfoResults.forEach((res, i) => {
        if (res.status === 'fulfilled') {
          const imr = parseFloat(res.value?.data?.base_imr || '0');
          ordMaxLevMap[validAssets[i].orderlySymbol] = imr > 0 ? Math.floor(1 / imr) : 0;
        }
      });

      const obResults = await Promise.all(
        validAssets.map(async a => {
          try {
            const r = await fetch(`${DYDX_API}/orderbooks/perpetualMarket/${a.dydxTicker}`);
            const d: DydxOrderbook = await r.json();
            return { symbol: a.symbol, ob: d };
          } catch { return { symbol: a.symbol, ob: null }; }
        })
      );
      const obMap: Record<string, DydxOrderbook | null> = {};
      obResults.forEach(x => { obMap[x.symbol] = x.ob; });

      const built: Row[] = validAssets.map(a => {
        const dydxMkt = dydxMap[a.dydxTicker];
        const ordFut  = ordMap[a.orderlySymbol];
        const ob      = obMap[a.symbol];

        const dydxBid = ob?.bids?.[0] ? parseFloat(ob.bids[0].price) : parseFloat(dydxMkt.oraclePrice);
        const dydxAsk = ob?.asks?.[0] ? parseFloat(ob.asks[0].price) : parseFloat(dydxMkt.oraclePrice);
        const dydxMid = (dydxBid + dydxAsk) / 2;
        const dydxSpreadPct   = dydxMid > 0 ? (dydxAsk - dydxBid) / dydxMid : 0;
        const dydxFunding     = parseFloat(dydxMkt.nextFundingRate);
        const orderlyMid      = ordFut.mark_price / a.multiplier;
        const orderlyFundingRaw = ordFut.est_funding_rate;
        const orderlyFunding  = orderlyFundingRaw / 8;

        const netS_D_L_O = dydxFunding - orderlyFunding;
        const netL_D_S_O = orderlyFunding - dydxFunding;
        let bestRate = 0;
        let bestDir: BestDir = 'NONE';
        if (netS_D_L_O >= netL_D_S_O && netS_D_L_O > 0) { bestRate = netS_D_L_O; bestDir = 'S_DYDX_L_ORD'; }
        else if (netL_D_S_O > 0)                          { bestRate = netL_D_S_O; bestDir = 'L_DYDX_S_ORD'; }

        return {
          symbol: a.symbol, dydxTicker: a.dydxTicker,
          orderlySymbol: a.orderlySymbol, multiplier: a.multiplier,
          dydxBid, dydxAsk, dydxFunding, dydxSpreadPct,
          orderlyMid, orderlyFunding, orderlyFundingRaw,
          orderlyNextFundingTime: ordFut.next_funding_time,
          netShortDydxLongOrd: netS_D_L_O, netLongDydxShortOrd: netL_D_S_O,
          bestRate, bestDir,
          dydxMaxLev: (() => { const imf = parseFloat(dydxMkt.initialMarginFraction || '0'); return imf > 0 ? Math.floor(1 / imf) : 0; })(),
          ordMaxLev: ordMaxLevMap[a.orderlySymbol] ?? 0,
        };
      });

      built.sort((a, b) => b.bestRate - a.bestRate);
      setRows(built);
      setLastUpdate(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const id = setInterval(fetchData, 30_000); return () => clearInterval(id); }, [fetchData]);

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-xs font-semibold uppercase tracking-widest rounded-t-lg transition-colors ${
      active ? 'bg-[#0a2560] text-[#4A9FE8] border-b-2 border-[#4A9FE8]' : 'text-blue-500 hover:text-blue-300'
    }`;

  const WsIndicator = () => (
    <div className="flex items-center gap-1.5 text-[11px]">
      {wsStatus === 'live'
        ? <><Wifi size={12} className="text-emerald-400" /><span className="text-emerald-500">BBO live</span></>
        : wsStatus === 'connecting'
        ? <><Wifi size={12} className="text-yellow-500 animate-pulse" /><span className="text-yellow-600">Bağlanıyor...</span></>
        : <><WifiOff size={12} className="text-rose-500" /><span className="text-rose-500">BBO offline</span></>
      }
    </div>
  );

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} p-6`}>

      {modalRow && (
        <OrderModal
          row={modalRow}
          bbo={bboMap[modalRow.orderlySymbol]}
          dark={dark}
          onClose={() => setModalRow(null)}
          sendWs={sendWs}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-black tracking-widest text-[#4A9FE8] uppercase">dYdX ↔ Raydium Perps</h1>
          <p className="text-xs text-blue-500 mt-0.5">Cross-DEX Funding Arbitrage · dYdX (1h) vs Orderly/Raydium (8h)</p>
        </div>
        <div className="flex items-center gap-3">
          <WsIndicator />
          {lastUpdate && <span className="text-[11px] text-blue-600">{lastUpdate.toLocaleTimeString('tr-TR')} güncellendi</span>}
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560] hover:bg-[#0d3080] text-blue-300 text-xs transition-colors">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />Yenile
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#0a2560] mb-5">
        <button className={tabClass(tab === 'dashboard')}  onClick={() => setTab('dashboard')}>Dashboard</button>
        <button className={tabClass(tab === 'performans')} onClick={() => setTab('performans')}>Performans</button>
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-rose-900/20 border border-rose-500/30 text-rose-400 text-xs">⚠ {error}</div>}

      {/* ── DASHBOARD ─────────────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          {/* Fee bar */}
          <div className="flex flex-wrap gap-4 text-[11px] text-blue-500 bg-[#061530]/60 rounded-xl border border-[#0a2560] px-4 py-2.5">
            <span>dYdX Maker: <span className="text-emerald-400 font-mono font-bold">{pct(DYDX_MAKER, 3)}</span></span>
            <span className="text-[#0a2560]">|</span>
            <span>dYdX Taker: <span className="text-rose-400 font-mono font-bold">{pct(DYDX_TAKER, 3)}</span></span>
            <span className="text-[#0a2560]">|</span>
            <span>Orderly Maker: <span className="text-emerald-400 font-mono font-bold">FREE</span></span>
            <span className="text-[#0a2560]">|</span>
            <span>Orderly Taker: <span className="text-rose-400 font-mono font-bold">{pct(ORDERLY_TAKER, 3)}</span></span>
            <span className="text-[#0a2560]">|</span>
            <span>Round-trip (maker): <span className="text-emerald-400 font-mono font-bold">{pct(DYDX_MAKER * 2, 4)}</span></span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-blue-500 text-sm py-8 justify-center">
              <RefreshCw size={14} className="animate-spin" /> Veriler yükleniyor...
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#0a2560]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#0a2560]">
                    <th className="text-left px-3 py-2.5 text-[#4A9FE8] font-semibold uppercase tracking-wider">Asset</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Bid</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Ask</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">Spread</th>
                    <th className="text-right px-3 py-2.5 text-blue-400 font-semibold">Funding 1h</th>
                    <th className="px-1" />
                    <th className="text-right px-2 py-2.5 text-emerald-400 font-semibold">Ord Bid</th>
                    <th className="text-right px-2 py-2.5 text-emerald-400 font-semibold">Ord Ask</th>
                    <th className="text-right px-2 py-2.5 text-emerald-400 font-semibold">Spread</th>
                    <th className="text-right px-3 py-2.5 text-emerald-400 font-semibold">Funding 1h</th>
                    <th className="px-1" />
                    <th className="text-right px-3 py-2.5 text-[#4A9FE8] font-semibold">Best Arb / h</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const bbo   = bboMap[row.orderlySymbol];
                    const ordBid = bbo ? bbo.bid / row.multiplier : null;
                    const ordAsk = bbo ? bbo.ask / row.multiplier : null;
                    const ordMid = ordBid && ordAsk ? (ordBid + ordAsk) / 2 : row.orderlyMid;
                    const ordSpread = ordBid && ordAsk && ordMid > 0 ? (ordAsk - ordBid) / ordMid : null;
                    return (
                      <tr key={row.symbol} className={`border-b border-[#0a2560]/50 hover:bg-[#0a2560]/30 transition-colors ${row.bestRate > 0.0001 ? 'bg-emerald-950/10' : ''}`}>
                        <td className="px-3 py-2.5">
                          <div className="font-bold text-white">{row.symbol}</div>
                          <div className="text-[9px] text-blue-600">{row.dydxTicker} / {row.orderlySymbol}</div>
                        </td>
                        <td className="text-right px-2 py-2.5 font-mono text-blue-200">{fmt(row.dydxBid)}</td>
                        <td className="text-right px-2 py-2.5 font-mono text-blue-200">{fmt(row.dydxAsk)}</td>
                        <td className="text-right px-2 py-2.5 font-mono text-blue-400 text-[11px]">{pct(row.dydxSpreadPct, 4)}</td>
                        <td className="text-right px-3 py-2.5"><FundingBadge rate={row.dydxFunding} /></td>
                        <td className="px-1"><div className="w-px h-8 bg-[#0a2560] mx-auto" /></td>
                        <td className="text-right px-2 py-2.5 font-mono text-emerald-300">
                          {ordBid !== null ? fmt(ordBid) : <span className="text-blue-700">{fmt(row.orderlyMid)}</span>}
                        </td>
                        <td className="text-right px-2 py-2.5 font-mono text-emerald-300">
                          {ordAsk !== null ? fmt(ordAsk) : <span className="text-blue-700">{fmt(row.orderlyMid)}</span>}
                        </td>
                        <td className="text-right px-2 py-2.5 font-mono text-emerald-600 text-[11px]">
                          {ordSpread !== null ? pct(ordSpread, 4) : <span className="text-blue-700">—</span>}
                        </td>
                        <td className="text-right px-3 py-2.5">
                          <div className="flex flex-col items-end gap-0.5">
                            <FundingBadge rate={row.orderlyFunding} />
                            <span className="text-[9px] text-blue-600">8h: <span className="font-mono">{pct(row.orderlyFundingRaw, 5)}</span></span>
                          </div>
                        </td>
                        <td className="px-1"><div className="w-px h-8 bg-[#0a2560] mx-auto" /></td>
                        <td className="text-right px-3 py-2.5"><ArbButton row={row} onOpen={setModalRow} /></td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={12} className="text-center py-8 text-blue-600">Ortak market bulunamadı</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-[10px] text-blue-600 pt-1">
            <span>Orderly BBO WebSocket ile 1s aralıklı güncelleme · Funding 8h periyotlu (÷8 = 1h eşdeğeri)</span>
            <span className="text-emerald-600">Maker ile round-trip = yalnızca dYdX maker × 2 = {pct(DYDX_MAKER * 2, 4)}</span>
          </div>
        </div>
      )}

      {tab === 'performans' && (
        <div className={`rounded-xl border ${t.cardBorder} ${t.card} p-6 text-center`}>
          <p className="text-blue-500 text-sm">Performans bölümü yakında eklenecek.</p>
        </div>
      )}
    </div>
  );
}
